// frontend/src/components/CartDrawer.jsx

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiX, FiTrash2, FiPlus, FiMinus } from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { getImageUrl } from '../utils/api';
import './CartDrawer.css';

const CartDrawer = ({ isOpen, onClose }) => {
    const {
        cart,
        removeFromCart,
        updateQuantity,
        totalAmount,
        totalItems,
        totalSavings,
        getEffectivePrice,
    } = useCart();

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Overlay */}
                    <motion.div
                        className="cart-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                    />

                    {/* Drawer */}
                    <motion.div
                        className="cart-drawer"
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'tween', duration: 0.35 }}
                    >
                        {/* ── Header ── */}
                        <div className="cart-header">
                            <div>
                                <h2 className="cart-title">Your Bag</h2>
                                <p className="cart-count">
                                    {totalItems} {totalItems === 1 ? 'item' : 'items'}
                                </p>
                            </div>
                            <button
                                className="cart-close"
                                onClick={onClose}
                                aria-label="Close cart"
                            >
                                <FiX size={22} />
                            </button>
                        </div>

                        {/* ── Items ── */}
                        <div className="cart-items">
                            {cart.length === 0 ? (
                                <div className="cart-empty">
                                    <div className="cart-empty-icon">🛍️</div>
                                    <p>Your bag is empty</p>
                                    <p className="cart-empty-sub">Add something beautiful</p>
                                    <button className="btn-primary" onClick={onClose}>
                                        Continue Shopping
                                    </button>
                                </div>
                            ) : (
                                cart.map((item, index) => {
                                    // FIX: use getEffectivePrice — respects discountPrice
                                    const effectivePrice = getEffectivePrice(item);
                                    const lineTotal = effectivePrice * item.quantity;

                                    return (
                                        <div key={index} className="cart-item">
                                            {/* FIX: image and name were not clickable at all —
                                                wrapped in a Link so the product actually opens,
                                                and closes the drawer so the page is visible */}
                                            <Link to={`/product/${item._id}`} onClick={onClose}>
                                                <img
                                                    src={
                                                        item.images?.[0]
                                                            ? getImageUrl(item.images[0])
                                                            : '/placeholder.jpg'
                                                    }
                                                    alt={item.name}
                                                    className="cart-item-img"
                                                    onError={e => { e.target.src = '/placeholder.jpg'; }}
                                                />
                                            </Link>

                                            <div className="cart-item-info">
                                                <Link to={`/product/${item._id}`} onClick={onClose}>
                                                    <h4>{item.name}</h4>
                                                </Link>
                                                {item.selectedSize && (
                                                    <p className="cart-item-meta">
                                                        Size: {item.selectedSize}
                                                    </p>
                                                )}
                                                {item.selectedColor && (
                                                    <p className="cart-item-meta">
                                                        Color: {item.selectedColor}
                                                    </p>
                                                )}

                                                {/* FIX: show correct line total using effectivePrice */}
                                                <p className="cart-item-price">
                                                    PKR {lineTotal.toLocaleString()}
                                                </p>

                                                <div className="cart-qty">
                                                    <button
                                                        className="cart-qty-btn"
                                                        onClick={() => updateQuantity(index, item.quantity - 1)}
                                                        aria-label="Decrease quantity"
                                                    >
                                                        <FiMinus size={12} />
                                                    </button>
                                                    <span className="cart-qty-count">
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        className="cart-qty-btn"
                                                        onClick={() => updateQuantity(index, item.quantity + 1)}
                                                        aria-label="Increase quantity"
                                                    >
                                                        <FiPlus size={12} />
                                                    </button>
                                                </div>
                                            </div>

                                            <button
                                                className="cart-item-remove"
                                                onClick={() => removeFromCart(index)}
                                                aria-label="Remove item"
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* ── Footer ── */}
                        {cart.length > 0 && (
                            <div className="cart-footer">
                                <div className="cart-total">
                                    <span>Total</span>
                                    <span>PKR {totalAmount.toLocaleString()}</span>
                                </div>

                                {/* Show savings if any discount applied */}
                                {totalSavings > 0 && (
                                    <p className="cart-shipping-note" style={{ color: '#2e7d32' }}>
                                        You save PKR {totalSavings.toLocaleString()} 🎉
                                    </p>
                                )}

                                <p className="cart-shipping-note">
                                    Shipping calculated at checkout
                                </p>

                                <Link
                                    to="/checkout"
                                    className="btn-primary cart-checkout-btn"
                                    onClick={onClose}
                                >
                                    Proceed to Checkout
                                </Link>
                                <button className="cart-continue" onClick={onClose}>
                                    Continue Shopping
                                </button>
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default CartDrawer;